import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ConnectivityService } from '../../core/pwa/connectivity.service';
import { installPrompt, isInstalled, promptInstall } from '../../core/pwa/install-prompt';
import { PwaUpdateService } from '../../core/pwa/pwa-update.service';
import { WebPushService } from '../../core/pwa/web-push.service';
import { OfflineQueueService } from '../../core/offline/offline-queue.service';
import { LanguageService } from '../../application/services/language.service';

const INSTALL_DISMISSED_KEY = 'pwa.install.dismissed';

/**
 * OPS-03-T01: what an installed app has to say for itself, in one place at the foot of the screen — a newer build is ready, the
 * connection is gone, changes are waiting to be sent (or one was refused), or the suite can be installed here (never twice,
 * never after a "not now"). It is added once, next to the support-session banner, so no screen repeats it.
 */
@Component({
  selector: 'app-pwa-prompts',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="fixed z-[9990] flex w-full max-w-md flex-col items-stretch gap-2 px-4" style="inset-inline-start: 50%; bottom: 1rem; transform: translateX(-50%);">
      @if (showUpdate()) {
        <div class="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-2 text-sm shadow-lg" role="status">
          <span class="min-w-0 flex-1">
            <strong class="block">{{ 'pwa.update.title' | translate }}</strong>
            <span class="block text-xs text-muted">{{ 'pwa.update.body' | translate }}</span>
          </span>
          <button type="button" class="btn-primary px-3 py-1 text-xs" data-test="pwa-update-reload" (click)="reload()">{{ 'pwa.update.reload' | translate }}</button>
          <button type="button" class="btn-ghost px-2 py-1 text-xs" (click)="dismissUpdate()">{{ 'pwa.update.later' | translate }}</button>
        </div>
      }

      @if (queue.count() > 0) {
        <div class="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-2 text-sm shadow-lg" role="status" data-test="pwa-pending">
          <span class="min-w-0 flex-1">{{ 'pwa.queue.pending' | translate: { count: queue.count() } }}</span>
          @if (online()) {
            <button type="button" class="btn-primary px-3 py-1 text-xs" data-test="pwa-send-now" (click)="sendNow()">{{ 'pwa.queue.sendNow' | translate }}</button>
          }
        </div>
      }

      @if (queue.refused(); as refused) {
        <div class="flex flex-wrap items-center gap-3 rounded-lg border border-danger-200 bg-danger-50 px-4 py-2 text-sm text-danger" role="alert" data-test="pwa-refused">
          <span class="min-w-0 flex-1">
            <strong class="block">{{ refused.reasonKey | translate }}</strong>
            <span class="block text-xs">{{ refused.labelKey | translate }}</span>
          </span>
          <button type="button" class="btn-ghost px-2 py-1 text-xs" data-test="pwa-refused-dismiss" (click)="queue.acknowledge()">{{ 'pwa.queue.dismiss' | translate }}</button>
        </div>
      }

      @if (!online()) {
        <div class="flex items-center gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-2 text-sm text-warning" role="status" data-test="pwa-offline">
          <span class="min-w-0 flex-1">{{ 'pwa.offline' | translate }}</span>
        </div>
      }

      @if (canInstall()) {
        <div class="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-2 text-sm shadow-lg" role="status">
          <span class="min-w-0 flex-1">
            <strong class="block">{{ 'pwa.install.title' | translate }}</strong>
            <span class="block text-xs text-muted">{{ 'pwa.install.body' | translate }}</span>
          </span>
          <button type="button" class="btn-primary px-3 py-1 text-xs" data-test="pwa-install" (click)="install()">{{ 'pwa.install.accept' | translate }}</button>
          <button type="button" class="btn-ghost px-2 py-1 text-xs" data-test="pwa-install-later" (click)="dismissInstall()">{{ 'pwa.install.later' | translate }}</button>
        </div>
      }
    </div>
  `,
})
export class PwaPromptsComponent implements OnInit {
  private readonly connectivity = inject(ConnectivityService);
  private readonly updates = inject(PwaUpdateService);
  private readonly push = inject(WebPushService);
  private readonly language = inject(LanguageService);
  readonly queue = inject(OfflineQueueService);

  readonly online = this.connectivity.online;
  /** Set by the dismiss buttons: the two offers are answers, not something to ask again on every page. */
  private readonly installDismissed = signal(readDismissed());
  private readonly updateHidden = signal(false);

  readonly showUpdate = computed(() => this.updates.updateReady() && !this.updateHidden());

  readonly canInstall = computed(() => !!installPrompt() && !this.installDismissed() && !isInstalled());

  ngOnInit(): void {
    this.updates.start();
    this.queue.start(); // OPS-03: sends what the last session could not, as soon as there is a connection
    this.reportThisBrowser();
  }

  sendNow(): void {
    void this.queue.replay();
  }

  /**
   * A browser signs in as one user after another. The endpoint and its keys belong to whoever is signed in now, so an existing
   * subscription is reported again with every sign-in — otherwise the first login keeps receiving the second one's notices.
   */
  private reportThisBrowser(): void {
    if (!sessionStorage.getItem('token')) return; // the sign-in page: nothing to report to
    void this.push.sync(this.language.getCurrent()).catch(() => undefined);
  }

  reload(): void {
    void this.updates.reload();
  }

  dismissUpdate(): void {
    this.updateHidden.set(true);
  }

  dismissInstall(): void {
    this.installDismissed.set(true);
    writeDismissed();
  }

  async install(): Promise<void> {
    const outcome = await promptInstall();
    // A refused browser dialog is the same answer as "not now": asking again on the next page would be nagging.
    if (outcome !== 'accepted') this.dismissInstall();
  }
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === '1';
  } catch {
    return true; // storage blocked: never offer it, rather than offer it on every page
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, '1');
  } catch {
    /* storage blocked: the answer holds for this tab only */
  }
}
